import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { UserPlus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { roleTermFor } from '../lib/roleTerm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tenant-only widget: lets the account holder authorize up to 3 additional users
 * (e.g., spouse, roommate, parent) on their unit.
 */
export default function AuthorizedUsersCard() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [maxUsers, setMaxUsers] = useState(3);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', relationship: '' });
  const loadedRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tenant/authorized-users`, { credentials: 'include' });
      const data = await r.json();
      if (r.ok) {
        setList(data.authorized_users || []);
        setMaxUsers(data.max || 3);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tenant/authorized-users`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Could not add user.');
      setList(data.authorized_users || []);
      setForm({ first_name: '', last_name: '', email: '', phone: '', relationship: '' });
      toast.success('Authorized user added.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/tenant/authorized-users/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Could not remove user.');
      setList(data.authorized_users || []);
      toast.success('Authorized user removed.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const limitReached = list.length >= maxUsers;

  return (
    <Card className="p-6" data-testid="authorized-users-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-[#C9A962]" />
        <h3 className="text-lg font-bold">Authorized Users</h3>
        <span className="ml-auto text-xs text-zinc-500" data-testid="auth-users-count">
          {list.length} of {maxUsers} used
        </span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        As the account holder, you can authorize up to {maxUsers} additional people on your unit.
      </p>

      {/* existing list */}
      {loading ? (
        <div className="text-sm text-zinc-500 py-4">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4">No authorized users yet.</div>
      ) : (
        <ul className="space-y-2 mb-4">
          {list.map((u) => (
            <li
              key={u.id}
              className="flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2"
              data-testid={`auth-user-${u.id}`}
            >
              <div>
                <div className="text-sm font-semibold text-zinc-900">
                  {u.first_name} {u.last_name}
                  <span className="ml-2 text-xs font-bold text-zinc-700" data-testid={`auth-user-type-${u.id}`}>{roleTermFor(user?.property_type)} A/U</span>
                  {u.relationship ? <span className="ml-2 text-xs text-zinc-500">· {u.relationship}</span> : null}
                </div>
                <div className="text-xs text-zinc-500">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(u.id)}
                className="p-2 text-zinc-400 hover:text-red-600 transition"
                aria-label="Remove authorized user"
                data-testid={`auth-user-remove-${u.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* add form */}
      {!limitReached && (
        <form onSubmit={submit} className="border-t border-zinc-200 pt-4 space-y-3" data-testid="auth-user-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="auth_first">First name *</Label>
              <Input id="auth_first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="auth-user-first" />
            </div>
            <div>
              <Label htmlFor="auth_last">Last name *</Label>
              <Input id="auth_last" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="auth-user-last" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="auth_email">Email *</Label>
              <Input id="auth_email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-user-email" />
            </div>
            <div>
              <Label htmlFor="auth_phone">Phone</Label>
              <Input id="auth_phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="auth-user-phone" />
            </div>
          </div>
          <div>
            <Label htmlFor="auth_rel">Relationship (optional)</Label>
            <Input
              id="auth_rel"
              value={form.relationship}
              onChange={(e) => setForm({ ...form, relationship: e.target.value })}
              placeholder="e.g., Spouse, Roommate, Parent"
              data-testid="auth-user-relationship"
            />
          </div>
          <Button
            type="submit"
            disabled={adding}
            className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
            data-testid="auth-user-add-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {adding ? 'Adding…' : 'Add Authorized User'}
          </Button>
        </form>
      )}

      {limitReached && (
        <div
          className="border-t border-zinc-200 pt-4 text-xs text-zinc-500"
          data-testid="auth-user-limit-msg"
        >
          You've reached the limit of {maxUsers} authorized users. Remove someone to add another.
        </div>
      )}
    </Card>
  );
}
